import clsx from 'clsx'
import { urlForImage } from 'lib/sanity.image'
import Image from 'next/image'
import Link from 'next/link'

import { Button } from 'components/Global/Button'
import { PortableText } from '@portabletext/react'
import { Hotspots } from 'components/Global/Hotspots'
import { Tag } from 'components/Global/Tag'
import { getUrlFromLink } from 'lib/helpers'

export const FeatureBlock = ({ layout = 'inset', items }) => {
  const containerClasses = clsx(
    'overflow-hidden w-full grid grid-cols-1',
    { 'p-20 gap-[15px] md:gap-20': layout === 'inset' },
    { 'md:grid-cols-2': items.length === 2 }
  )
  return (
    <div className={containerClasses}>
      {items.map((item) => {
        if (item._type === 'featureImage') {
          return <FeatureImage key={item._key} layout={layout} {...item} />
        }
        if (item._type === 'featureText') {
          return <FeatureText key={item._key} layout={layout} {...item} />
        }
        if (item._type === 'featureImageText') {
          return <FeatureImageText key={item._key} layout={layout} {...item} />
        }
        return null
      })}
    </div>
  )
}

const FeatureBlockCard = ({ tag, title, subtitle, link, image }) => {
  const url = getUrlFromLink(link)
  return (
    <Link
      scroll={false}
      href={url}
      className="ease group ml-20 inline-flex gap-10 rounded-12 bg-[rgba(255,255,255,0.75)] p-10 backdrop-blur-md transition-all duration-300 active:-translate-y-[6px] active:bg-whiter md:hover:-translate-y-[6px] md:hover:bg-whiter"
    >
      {image ? (
        <div className="relative aspect-[2/2.75] w-[52px] overflow-hidden rounded-6">
          <Image
            src={urlForImage(image).width(1600).auto('format').url()}
            fill
            alt=""
            style={{ objectFit: 'cover' }}
            placeholder="blur"
            blurDataURL={image.lqip}
            sizes="120px"
          />
        </div>
      ) : null}
      <div className="flex max-w-[350px] flex-col">
        <Tag className="self-start bg-[#F0F0F0]">{tag}</Tag>
        <div className="pt-12 text-12 leading-100 text-midGray">{title}</div>
        <div className="pt-4 text-12 leading-100 text-gray">{subtitle}</div>
      </div>
      {link ? (
        <Button className="ease ml-20 self-stretch bg-yellow px-20 text-11 text-midGray opacity-0 transition-opacity duration-300 hover:!bg-yellow group-active:opacity-100 md:group-hover:opacity-100">
          {link.label}
        </Button>
      ) : null}
    </Link>
  )
}

const FeatureImageText = ({
  layout,
  mediaType = 'image',
  image,
  video,
  cards,
}) => {
  const tagContent = (
    <div className="absolute bottom-0 left-0 z-[2] w-full overflow-x-scroll overscroll-contain whitespace-nowrap py-20 pr-20">
      {cards?.map((card) => (
        <FeatureBlockCard key={card._key} {...card} />
      ))}
    </div>
  )

  if (mediaType === 'video' && video?.url) {
    return (
      <div
        className={`relative h-[85vh] w-full md:aspect-[var(--aspect-ratio)] md:h-auto  md:w-full ${
          layout === 'inset'
            ? 'overflow-hidden rounded-12 shadow-md md:rounded-24'
            : ''
        }`}
      >
        <video
          src={video.url}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 z-[1] h-full w-full object-cover"
        />
        {tagContent}
      </div>
    )
  }

  return (
    <div
      className={`relative h-[85vh] w-full md:aspect-[var(--aspect-ratio)] md:h-auto md:max-h-[100vh] md:w-full ${
        layout === 'inset'
          ? 'overflow-hidden rounded-12 shadow-md md:rounded-24'
          : ''
      }`}
      style={
        {
          '--aspect-ratio': `${image.width}/${image.height}`,
        } as any
      }
    >
      <Image
        src={urlForImage(image).width(1000).auto('format').url()}
        alt={image.alt}
        fill
        style={{ objectFit: 'cover' }}
        placeholder="blur"
        blurDataURL={image.lqip}
      />
      {tagContent}
    </div>
  )
}

const FeatureImage = ({ image, layout, hotspots }) => {
  const url = urlForImage(image).width(1000).auto('format').url()
  return (
    <div
      className={`relative aspect-[var(--aspect-ratio)] w-full  ${
        layout === 'inset'
          ? 'overflow-hidden rounded-12 shadow-md md:rounded-24'
          : ''
      }`}
      style={
        {
          '--aspect-ratio': `${image.width}/${image.height}`,
        } as any
      }
    >
      <Image
        src={url}
        alt={image.alt}
        fill
        style={{ objectFit: 'cover' }}
        placeholder="blur"
        blurDataURL={image.lqip}
      />
      <Hotspots spots={hotspots} />
    </div>
  )
}

const FeatureText = ({ layout, title, topText, bottomText }) => {
  return (
    <div
      className={`flex min-h-[100svh] flex-col items-center justify-between bg-whiter p-26 text-center uppercase text-darkGray md:min-h-[unset] ${
        layout === 'inset'
          ? 'overflow-hidden rounded-12 shadow-md md:rounded-24'
          : ''
      }`}
    >
      <div className="pt-20 leading-130">
        <PortableText value={topText} />
      </div>
      <div className="text-[40px] uppercase leading-90 md:text-[7vw]">
        {title}
      </div>
      <div className="pb-20 leading-130">
        <PortableText value={bottomText} />
      </div>
    </div>
  )
}
